import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams } from '@tanstack/react-router'
import {
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Circle,
  ExternalLink,
  Gauge,
  Image as ImageIcon,
  Mail,
  MapPin,
  Send,
  Sparkles,
  Truck,
  User as UserIcon,
} from 'lucide-react'
import { useState } from 'react'

import { AppShell } from '@/components/layout/app-shell'
import type { ClientLite } from '@/components/planning/planning-types'
import type { MissionDetail } from '@/components/missions/mission-types'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
} from '@/components/ui/card'
import { ImageLightbox } from '@/components/ui/image-lightbox'
import { Spinner } from '@/components/ui/spinner'
import { useApi } from '@/hooks/use-api'
import { type ApiClient, ApiError } from '@/lib/api'
import {
  formatBonusMinutes,
  formatDateLong,
  formatDateTime,
  formatHours,
  formatMinutes,
  formatMoney,
  trimSeconds,
} from '@/lib/format'
import { cn } from '@/lib/utils'

/**
 * Mission detail — admin view.
 *
 * Two-column desktop layout:
 *
 *   ┌──────── Infos / Slots / Totals / Bonus / Lien planning
 *   └──────── Signatures / Photo bon / Statut / Email / Audit
 *
 * The right column carries the workflow toggles (`verified` and
 * `readyToSend`). They use the same optimistic mutation pattern
 * as the listing — flip immediately, network in the background,
 * roll back on failure.
 *
 * Read-only otherwise: the V2 product rule (inherited from V1)
 * is that a filed mission is a legal document. To correct one,
 * the driver files a new one. We may add a "duplicate" or
 * "annule et remplace" workflow later if the field reports it,
 * but not at MVP.
 */

interface DetailContextProps {
  mission: MissionDetail
  clients: readonly ClientLite[]
  onToggleVerified: (next: boolean) => void
  onToggleReady: (next: boolean) => void
  toggleError: string | null
}

export function MissionDetailPage() {
  const api = useApi()
  const queryClient = useQueryClient()
  const navigate = useNavigate()

  // Same `strict: false` trick as the driver detail page — the
  // typed `from` would couple us to the protected layout's id
  // ('authed'), which is an internal detail.
  const params = useParams({ strict: false }) as { id?: string }
  const id = params.id ?? ''

  const detailQuery = useQuery({
    queryKey: ['missions', 'detail', id] as const,
    queryFn: () => api.get<MissionDetail>(`/api/missions/${id}`),
    enabled: id !== '',
    staleTime: 30 * 1000,
  })

  // Pre-load clients so the header can show the client colour
  // pill without a follow-up fetch.
  const clientsQuery = useQuery({
    queryKey: ['clients', 'lite'] as const,
    queryFn: () =>
      api.get<ClientLite[]>('/api/clients', {
        // 100 = the API's hardcoded ceiling. If a tenant ever
        // crosses 100 clients we'll switch to a paginated
        // SelectSearch — the picker already filters client-side
        // so the upgrade is purely a data-fetch concern.
        limit: 100,
        sortBy: 'name',
        sortOrder: 'asc',
      }),
    staleTime: 5 * 60 * 1000,
  })

  const [toggleError, setToggleError] = useState<string | null>(null)

  const verifyMutation = useDetailToggle(api, queryClient, id, 'verify', 'verified', setToggleError)
  const readyMutation = useDetailToggle(api, queryClient, id, 'ready', 'readyToSend', setToggleError)

  return (
    <AppShell>
      <button
        type="button"
        onClick={() => void navigate({ to: '/missions' })}
        className={cn(
          'inline-flex items-center gap-2 mb-4',
          'h-9 px-2 -ml-2 rounded-md text-sm',
          'text-text-muted hover:text-text hover:bg-surface-hover',
        )}
      >
        <ArrowLeft size={16} aria-hidden="true" />
        Retour aux missions
      </button>

      {detailQuery.isLoading ? (
        <div className="py-16 flex justify-center">
          <Spinner size={28} label="Chargement de la mission" />
        </div>
      ) : detailQuery.isError ? (
        <Card>
          <CardContent>
            <p className="text-danger">
              Mission introuvable. Elle a peut-être été supprimée.
            </p>
          </CardContent>
        </Card>
      ) : detailQuery.data !== undefined ? (
        <DetailLayout
          mission={detailQuery.data}
          clients={clientsQuery.data ?? []}
          onToggleVerified={(next) =>
            verifyMutation.mutate({ value: next })
          }
          onToggleReady={(next) =>
            readyMutation.mutate({ value: next })
          }
          toggleError={toggleError}
        />
      ) : null}
    </AppShell>
  )
}

/* -------------------------------------------------------------------------- */
/*  Two-column body                                                            */
/* -------------------------------------------------------------------------- */

function DetailLayout({
  mission,
  clients,
  onToggleVerified,
  onToggleReady,
  toggleError,
}: DetailContextProps) {
  const navigate = useNavigate()
  const clientColor =
    clients.find((c) => c.id === mission.clientId)?.color ?? null

  return (
    <div className="flex flex-col gap-4">
      {/* Header */}
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">{mission.site}</h1>
          <p className="text-sm text-text-muted mt-1">
            {formatDateLong(mission.date)}
          </p>
        </div>
        <div className="flex items-center gap-1.5">
          {mission.isDaily ? <Badge tone="template">Journalier</Badge> : null}
          {mission.verified ? (
            <Badge tone="success">
              <CheckCircle2 size={12} aria-hidden="true" />
              Vérifié
            </Badge>
          ) : null}
          {mission.readyToSend ? (
            <Badge tone="info">
              <Send size={12} aria-hidden="true" />
              Prêt à envoyer
            </Badge>
          ) : null}
        </div>
      </header>

      {toggleError !== null ? (
        <p
          role="alert"
          className="text-sm text-danger bg-danger-soft border border-danger/30 rounded-md px-3 py-2"
        >
          {toggleError}
        </p>
      ) : null}

      {/* Two-column grid: 60/40 split on lg+, stacked below. */}
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
        {/* Left: 3 cols out of 5 ≈ 60% */}
        <div className="lg:col-span-3 flex flex-col gap-4">
          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
                Informations
              </h2>
            </CardHeader>
            <CardContent className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <KeyValue
                icon={<MapPin size={16} />}
                label="Chantier"
                value={mission.site}
              />
              <KeyValue
                icon={<Calendar size={16} />}
                label="Date"
                value={formatDateLong(mission.date)}
              />
              <KeyValue
                icon={<UserIcon size={16} />}
                label="Chauffeur"
                value={mission.driverName ?? '—'}
              />
              <KeyValue
                icon={<Truck size={16} />}
                label="Véhicule"
                value={mission.vehicleRegistration ?? '—'}
              />
              <KeyValue
                icon={
                  <span
                    aria-hidden="true"
                    className="inline-block w-3 h-3 rounded-full"
                    style={{ background: clientColor ?? '#cbd5e1' }}
                  />
                }
                label="Client"
                value={mission.clientName ?? '—'}
              />
              <KeyValue
                icon={<Gauge size={16} />}
                label="Mission journalière"
                value={mission.isDaily ? 'Oui' : 'Non'}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
                Créneaux horaires
              </h2>
            </CardHeader>
            <CardContent className="flex flex-col gap-2">
              <SlotRow
                label="Matin"
                start={mission.morningStart}
                end={mission.morningEnd}
              />
              <SlotRow
                label="Après-midi / soir"
                start={mission.afternoonStart}
                end={mission.afternoonEnd}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
                Totaux
              </h2>
            </CardHeader>
            <CardContent className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <Stat label="Total" value={formatHours(mission.totalHours)} primary />
              <Stat label="Jour" value={formatHours(mission.dayHours)} />
              <Stat label="Nuit" value={formatHours(mission.nightHours)} />
              {mission.bonusMinutes > 0 ? (
                <Stat
                  label="Bonus"
                  value={formatBonusMinutes(mission.bonusMinutes)}
                />
              ) : null}
            </CardContent>
          </Card>

          {/* Bonus + extras only when there's something to show. */}
          {mission.washing ||
          mission.travel !== '' ||
          mission.tollAmount !== null ? (
            <Card>
              <CardHeader>
                <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
                  Bonus &amp; extras
                </h2>
              </CardHeader>
              <CardContent className="flex flex-col gap-2">
                {mission.washing ? (
                  <KeyValue
                    icon={<Sparkles size={16} />}
                    label="Lavage"
                    value={`Oui (+${formatMinutes(mission.washingMinutes)})`}
                  />
                ) : null}
                {mission.travel !== '' ? (
                  <KeyValue
                    icon={<Sparkles size={16} />}
                    label="Déplacement"
                    value={`${mission.travel} (+${formatMinutes(mission.travelMinutes)})`}
                  />
                ) : null}
                {mission.tollAmount !== null ? (
                  <KeyValue
                    icon={<Sparkles size={16} />}
                    label="OBU / Péage"
                    value={formatMoney(mission.tollAmount)}
                  />
                ) : null}
              </CardContent>
            </Card>
          ) : null}

          {/* Link to source planning when present. */}
          {mission.planningId !== null ? (
            <Card>
              <CardContent className="flex items-center justify-between gap-3 py-3">
                <span className="text-sm text-text-muted">
                  Cette mission a été remplie depuis un planning.
                </span>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => void navigate({ to: '/planning' })}
                >
                  Voir le planning
                  <ExternalLink size={14} />
                </Button>
              </CardContent>
            </Card>
          ) : null}
        </div>

        {/* Right: 2 cols out of 5 ≈ 40% */}
        <div className="lg:col-span-2 flex flex-col gap-4">
          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
                Statut &amp; workflow
              </h2>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <ToggleRow
                label="Vérifié"
                Icon={CheckCircle2}
                tone="success"
                checked={mission.verified}
                onChange={onToggleVerified}
              />
              <ToggleRow
                label="Prêt à envoyer"
                Icon={Send}
                tone="info"
                checked={mission.readyToSend}
                onChange={onToggleReady}
              />
              {mission.supervisorEmail !== null ? (
                <KeyValue
                  icon={<Mail size={16} />}
                  label="Email chef de chantier"
                  value={mission.supervisorEmail}
                />
              ) : null}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
                Signatures
              </h2>
            </CardHeader>
            <CardContent className="flex flex-col gap-3">
              <SignatureBlock
                label="Chef de chantier"
                url={mission.supervisorSignatureUrl}
              />
              <SignatureBlock
                label="Chauffeur"
                url={mission.driverSignatureUrl}
              />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
                Photo du bon
              </h2>
            </CardHeader>
            <CardContent>
              <ReceiptBlock url={mission.receiptPhotoUrl} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <h2 className="text-sm font-semibold uppercase tracking-wide text-text-muted">
                Métadonnées
              </h2>
            </CardHeader>
            <CardContent className="flex flex-col gap-1 text-sm text-text-muted">
              <p>
                Créée le{' '}
                <span className="text-text">{formatDateTime(mission.createdAt)}</span>
              </p>
              <p>
                Modifiée le{' '}
                <span className="text-text">{formatDateTime(mission.updatedAt)}</span>
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/*  Building blocks                                                            */
/* -------------------------------------------------------------------------- */

function KeyValue({
  icon,
  label,
  value,
}: {
  icon: React.ReactNode
  label: string
  value: string
}) {
  return (
    <div className="flex items-start gap-2">
      <span className="text-text-muted mt-0.5 shrink-0" aria-hidden="true">
        {icon}
      </span>
      <div className="flex flex-col min-w-0">
        <span className="text-xs text-text-muted">{label}</span>
        <span className="text-sm text-text break-words">{value}</span>
      </div>
    </div>
  )
}

function SlotRow({
  label,
  start,
  end,
}: {
  label: string
  start: string | null
  end: string | null
}) {
  const filled = start !== null && end !== null
  return (
    <div className="flex items-center justify-between min-h-[2rem] text-sm">
      <span className="text-text-muted">{label}</span>
      {filled ? (
        <span className="font-mono tabular-nums text-text">
          {trimSeconds(start)} → {trimSeconds(end)}
        </span>
      ) : (
        <span className="text-text-muted">—</span>
      )}
    </div>
  )
}

function Stat({
  label,
  value,
  primary,
}: {
  label: string
  value: string
  primary?: boolean
}) {
  return (
    <div
      className={cn(
        'rounded-md p-3',
        primary ? 'bg-info-soft text-info' : 'bg-surface-muted text-text',
      )}
    >
      <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
      <p
        className={cn(
          'font-mono tabular-nums',
          primary ? 'text-2xl font-bold' : 'text-lg font-semibold',
        )}
      >
        {value}
      </p>
    </div>
  )
}

function ToggleRow({
  label,
  Icon,
  tone,
  checked,
  onChange,
}: {
  label: string
  Icon: typeof CheckCircle2
  tone: 'success' | 'info'
  checked: boolean
  onChange: (next: boolean) => void
}) {
  return (
    <div className="flex items-center justify-between gap-3">
      <span className="text-sm font-medium text-text">{label}</span>
      <button
        type="button"
        role="switch"
        aria-checked={checked}
        aria-label={
          checked
            ? `Désactiver "${label}"`
            : `Activer "${label}"`
        }
        onClick={() => onChange(!checked)}
        className={cn(
          'inline-flex items-center justify-center',
          'h-9 w-9 rounded-full transition-all',
          'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand focus-visible:ring-offset-2',
          checked
            ? tone === 'success'
              ? 'text-success bg-success-soft hover:bg-success/20'
              : 'text-info bg-info-soft hover:bg-info/20'
            : 'text-text-muted hover:bg-surface-muted',
        )}
      >
        {checked ? (
          <Icon size={20} aria-hidden="true" />
        ) : (
          <Circle size={20} aria-hidden="true" />
        )}
      </button>
    </div>
  )
}

function SignatureBlock({ label, url }: { label: string; url: string | null }) {
  const [open, setOpen] = useState(false)
  if (url === null) {
    return (
      <div className="flex flex-col gap-1.5">
        <span className="text-xs text-text-muted">{label}</span>
        <div className="bg-surface-muted border border-dashed border-surface-border rounded-md py-6 text-center text-sm text-text-muted">
          Pas de signature
        </div>
      </div>
    )
  }
  return (
    <div className="flex flex-col gap-1.5">
      <span className="text-xs text-text-muted">{label}</span>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={`Voir la signature ${label} en plein écran`}
        className="bg-surface border border-surface-border rounded-md p-2 hover:bg-surface-hover transition-colors"
      >
        <img
          src={url}
          alt={label}
          className="w-full h-32 object-contain"
        />
      </button>
      <ImageLightbox src={url} alt={label} open={open} onOpenChange={setOpen} />
    </div>
  )
}

function ReceiptBlock({ url }: { url: string | null }) {
  const [open, setOpen] = useState(false)
  if (url === null) {
    return (
      <div className="bg-surface-muted border border-dashed border-surface-border rounded-md py-8 text-center">
        <ImageIcon size={24} className="mx-auto text-text-muted opacity-60 mb-1" aria-hidden="true" />
        <p className="text-sm text-text-muted">Aucune photo de bon</p>
      </div>
    )
  }
  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label="Voir la photo en plein écran"
        className="block w-full bg-surface border border-surface-border rounded-md overflow-hidden hover:bg-surface-hover transition-colors"
      >
        <img
          src={url}
          alt="Bon de livraison"
          className="w-full max-h-[280px] object-contain bg-surface-muted"
        />
      </button>
      <ImageLightbox src={url} alt="Bon de livraison" open={open} onOpenChange={setOpen} />
    </>
  )
}

/* -------------------------------------------------------------------------- */
/*  Toggle mutation factory (detail page variant)                              */
/* -------------------------------------------------------------------------- */

function useDetailToggle(
  api: ApiClient,
  queryClient: ReturnType<typeof useQueryClient>,
  id: string,
  endpoint: 'verify' | 'ready',
  field: 'verified' | 'readyToSend',
  onErrorMessage: (msg: string) => void,
) {
  const queryKey = ['missions', 'detail', id] as const
  return useMutation({
    mutationFn: ({ value }: { value: boolean }) =>
      api.patch<unknown>(`/api/missions/${id}/${endpoint}`, { value }),
    onMutate: async ({ value }) => {
      await queryClient.cancelQueries({ queryKey })
      const prev = queryClient.getQueryData<MissionDetail>(queryKey)
      if (prev !== undefined) {
        queryClient.setQueryData<MissionDetail>(queryKey, {
          ...prev,
          [field]: value,
        })
      }
      return { prev }
    },
    onError: (err, _vars, ctx) => {
      if (ctx?.prev !== undefined) queryClient.setQueryData(queryKey, ctx.prev)
      onErrorMessage(
        err instanceof ApiError
          ? err.message
          : "Modification impossible. Veuillez réessayer.",
      )
    },
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey })
      // Also invalidate listing caches so the table reflects the
      // change next time the admin goes back.
      void queryClient.invalidateQueries({ queryKey: ['missions', 'list'] })
    },
  })
}
